import { useState, useEffect } from "react";
import moment from "moment";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Line } from "react-chartjs-2";
// Redux
import { useSelector } from "react-redux";
// Constants - functions
import { pathHomePage, WEB_SERVICE_MIMS, colorsArray } from "../../../../config/constants";
import { getTrainedMinutesAndSeconds } from "../../../../config/functions";
import { getProtocolTitle, getProtocolInfoPrefix } from "../../../../config/validateProtocols";
// Components
import {
  MainContainer,
  BackLink,
  ArrowImg,
  LinkText,
  PreviousAndNextDiv,
  NavigationButton,
  SeparatorDiv,
  SessionHeaderMainDiv,
  SessionHeaderDiv,
  SessionTitle,
  SessionHideDiv,
  SessionMainDiv,
  SessionDiv,
  InfoDiv,
  SessionText,
  ShowExtraInfoButton,
  ShowExtraInfoImg,
  ExtraInfoDiv,
  ExtraInfoSessionDiv,
  ExtraInfoSessionTitle,
  IndicatorDataDiv,
  IndicatorDataText,
} from "./styles";
import Loader from "../../../loader";
import Axios from "../../../../config/axios";
import PatientNavigation from "../../../patientNavigation";
import Title from "../../../title";
import Graphic from "../../../graphic";
import GameModeLegend from "../gameModeLegend";
// Assets
import ArrowBackImg from "../../../../assets/icons/ico_arrow_back.svg";
import ArrowLeftImg from "../../../../assets/icons/ico_arrow_left_bold.svg";
import ArrowRightImg from "../../../../assets/icons/ico_arrow_right_bold.svg";
import IcoArrowUp from "../../../../assets/icons/ico_arrow_up.svg";
import IcoArrowDown from "../../../../assets/icons/ico_arrow_down.svg";

const SessionGraphics = () => {
  const [session, setSession] = useState([]);
  const [sessionsId, setSessionsId] = useState([]);
  const [sessionNumber, setSessionNumber] = useState(0);
  const [isLoadingDifficultyModulators, setIsLoadingDifficultyModulators] = useState(true);
  // Set Difficulty Modulators
  const [difficultyModulators, setDifficultyModulators] = useState([]);

  const [showExtraInfo, setShowExtraInfo] = useState(false);
  const { software, patientId, protocolId, sessionId } = useParams();
  const clinicianToken = useSelector(({ clinician }) => clinician.data.token);
  const [t] = useTranslation("global");

  // Fetching protocol sessions
  useEffect(() => {
    Axios("get", `${WEB_SERVICE_MIMS}/sessions/id/${patientId}/${protocolId}/${software}`)
      .then(({ data }) => {
        let settingSessionsId = [];
        data.forEach((session) => {
          settingSessionsId.push(session.SESSION_ID);
        });
        setSessionNumber(settingSessionsId.indexOf(Number(sessionId)) + 1);
        setSessionsId(settingSessionsId);
      })
      .catch((error) => {
        console.log("Endpoint to get sessions id: ", error);
      });
  }, [patientId, protocolId, software, sessionId]);

  // Fetch session data
  useEffect(() => {
    Axios("get", `${WEB_SERVICE_MIMS}/reports/by-session/${sessionId}/${software}`)
      .then(({ data }) => {
        let currentSession = {};
        data.forEach((recording) => {
          switch (recording.RECORDING_KEY) {
            case "score":
              currentSession.date = recording.STARTING_DATE;
              currentSession.score = Number(recording.RECORDING_VALUE);
              break;
            case "sessionDuration(seconds)":
              currentSession.duration = Number(recording.RECORDING_VALUE);
              break;
            default:
              break;
          }
        });
        setSession(currentSession);
      })
      .catch((error) => {
        console.log("Endpoint to get protocol recordings: ", error);
      });
  }, [sessionId, software]);

  // Fetch Difficulty Modulators
  useEffect(() => {
    Axios("get", `${WEB_SERVICE_MIMS}/difficulty-modulators/by-session/${protocolId}/${patientId}/${sessionId}/${software}`)
      .then(({ data }) => {
        let difficultyModulatorsByGameMode = [];
        let currentDifficultyModulators = [];
        let currentGameMode = "";
        data.forEach((dm, dmIndex) => {
          if (currentGameMode !== "" && currentGameMode !== dm.GAME_MODE) {
            difficultyModulatorsByGameMode.push(currentDifficultyModulators);
            currentDifficultyModulators = [];
          }
          currentGameMode = dm.GAME_MODE;
          currentDifficultyModulators.push(dm);
          if (data.length === dmIndex + 1) difficultyModulatorsByGameMode.push(currentDifficultyModulators);
        });
        setDifficultyModulators(difficultyModulatorsByGameMode);
        setIsLoadingDifficultyModulators(false);
      })
      .catch((error) => {
        console.log("Endpoint to get Difficulty Modulators by session: ", error);
      });
  }, [protocolId, patientId, sessionId, software]);

  const sessionsNavigation = () => {
    const refreshSession = (newSessionId) => {
      window.location.href = `${pathHomePage}/session-graphics/${clinicianToken}/${software}/${patientId}/${protocolId}/${newSessionId}`;
    };

    const redirectPreviousSession = () => {
      const previousIdIndex = sessionsId.indexOf(Number(sessionId)) - 1;
      const previousId = sessionsId.at(previousIdIndex);
      refreshSession(previousId);
    };

    const redirectNextSession = () => {
      const nextIdIndex = sessionsId.indexOf(Number(sessionId)) + 1;
      let nextSessionId;
      if (nextIdIndex === sessionsId.length) {
        nextSessionId = sessionsId.shift();
      } else {
        nextSessionId = sessionsId.at(nextIdIndex);
      }
      refreshSession(nextSessionId);
    };

    return (
      <MainContainer>
        <BackLink to={`${pathHomePage}/reports/${clinicianToken}/${software}/${patientId}/${protocolId}`}>
          <ArrowImg src={ArrowBackImg} alt="ico-arrow-back" margin="0 10px 0 0" margin960="0 5px 0 0" />
          <LinkText>{`${t("reports_sessionNavigation_title")} (${t(getProtocolTitle("protocols", protocolId))})`}</LinkText>
        </BackLink>
        <PreviousAndNextDiv>
          <NavigationButton onClick={redirectPreviousSession}>
            <ArrowImg src={ArrowLeftImg} alt="ico-arrow-left" margin="0 10px 0 0" margin960="0 5px 0 0" />
            <LinkText>{t("previous")}</LinkText>
          </NavigationButton>
          <SeparatorDiv />
          <NavigationButton onClick={redirectNextSession}>
            <LinkText>{t("next")}</LinkText>
            <ArrowImg src={ArrowRightImg} alt="ico-arrow-right" margin="0 0 0 10px" margin960="0 0 0 5px" />
          </NavigationButton>
        </PreviousAndNextDiv>
      </MainContainer>
    );
  };

  const renderSessionDetails = () => {
    const date = moment(session.date).format("DD/MM/YYYY");
    const hour = moment(session.date).format("HH:mm:ss");
    const sessionDuration = getTrainedMinutesAndSeconds(Number(session.duration ? session.duration : 0));

    const handleShowExtraInfo = () => setShowExtraInfo(!showExtraInfo);
    return (
      <>
        {/* Header */}
        <SessionHeaderMainDiv>
          <SessionHeaderDiv>
            <SessionTitle width="100px" width910="75px">
              {t("session").toUpperCase()}
            </SessionTitle>
            <SessionTitle width="138px" width910="80px">
              {t("date").toUpperCase()}
            </SessionTitle>
            <SessionTitle width="108px" width910="80px">
              {t("time").toUpperCase()}
            </SessionTitle>
            <SessionHideDiv>
              <SessionTitle width="120px">{t("score").toUpperCase()}</SessionTitle>
              <SessionTitle width="105px">{t("duration").toUpperCase()}</SessionTitle>
            </SessionHideDiv>
          </SessionHeaderDiv>
        </SessionHeaderMainDiv>
        {/* Body: Session */}
        <SessionMainDiv>
          <SessionDiv>
            <InfoDiv>
              <SessionText width="100px" width910="75px">
                {sessionNumber}
              </SessionText>
              <SessionText width="138px" width910="80px">
                {date}
              </SessionText>
              <SessionText width="108px" width910="80px">
                {hour}
              </SessionText>
              <ShowExtraInfoButton onClick={handleShowExtraInfo}>
                <ShowExtraInfoImg src={showExtraInfo ? IcoArrowUp : IcoArrowDown} alt="ico-arrow" />
              </ShowExtraInfoButton>
            </InfoDiv>
            <ExtraInfoDiv show={showExtraInfo}>
              <ExtraInfoSessionDiv>
                <ExtraInfoSessionTitle>{t("score").toUpperCase()}</ExtraInfoSessionTitle>
                <SessionText width="120px" width910="auto">
                  {session.score}
                </SessionText>
              </ExtraInfoSessionDiv>
              <ExtraInfoSessionDiv>
                <ExtraInfoSessionTitle>{t("duration").toUpperCase()}</ExtraInfoSessionTitle>
                <SessionText width="105px" width910="auto">
                  {sessionDuration}
                </SessionText>
              </ExtraInfoSessionDiv>
            </ExtraInfoDiv>
          </SessionDiv>
        </SessionMainDiv>
      </>
    );
  };

  const difficultyModulatorsData = () => {
    let difficultyModulatorsDatasets = [];
    let currentColorIndex = 0;

    // Setting session seconds
    let sessionSeconds = [];
    const firstSessionSecond = Number(difficultyModulators[0][0].SECONDS_FROM_START);
    const lastSessionSecond = Number(difficultyModulators.at(-1).at(-1).SECONDS_FROM_START);
    for (let i = firstSessionSecond; i <= lastSessionSecond; i++) sessionSeconds.push(i);

    // Setting difficulty modulators
    difficultyModulators.forEach((computationsByGameMode) => {
      let difficultyModulatorsByGameMode = [];
      // Setting game mode
      const gameMode = computationsByGameMode[0].GAME_MODE;
      // Running session seconds
      sessionSeconds.forEach((sessionSecond) => {
        let currentDifficultyModulators = [];
        let currentDifficultyModulatorsValues = [];
        // Get a reverse array because is required only the last difficulty modulators of each game mode
        const dmByGameMode = computationsByGameMode.filter((dm) => dm.SECONDS_FROM_START === sessionSecond).reverse();
        dmByGameMode.forEach((dm) => {
          if (!currentDifficultyModulators.includes(dm.PARAMETER_KEY)) {
            currentDifficultyModulators.push(dm.PARAMETER_KEY);
            currentDifficultyModulatorsValues.push(Number(dm.PARAMETER_VALUE));
          }
        });
        // Getting difficulty modulators average
        let dmAverage = 0;
        currentDifficultyModulatorsValues.forEach((value) => {
          dmAverage += value;
        });
        dmAverage /= currentDifficultyModulatorsValues.length;
        difficultyModulatorsByGameMode.push(dmAverage);
      });
      // Setting a repeated game mode
      const repeatedGameMode = difficultyModulatorsDatasets.filter((difficultyModulatorsByGameMode) => {
        return difficultyModulatorsByGameMode.label === gameMode;
      });

      // Pushing each game mode input
      difficultyModulatorsDatasets.push({
        label: gameMode,
        data: difficultyModulatorsByGameMode,
        backgroundColor: repeatedGameMode.length > 0 ? repeatedGameMode[0].backgroundColor : [colorsArray[currentColorIndex]],
        borderColor: repeatedGameMode.length > 0 ? repeatedGameMode[0].borderColor : [colorsArray[currentColorIndex]],
        spanGaps: true,
      });
      // Setting next index color
      currentColorIndex === colorsArray.length - 1 ? (currentColorIndex = 0) : currentColorIndex++;
    });
    return {
      labels: sessionSeconds,
      datasets: difficultyModulatorsDatasets,
    };
  };

  const protocolPrefix = getProtocolInfoPrefix(protocolId);
  const chartData = difficultyModulators.length ? difficultyModulatorsData() : null;

  return (
    <>
      {isLoadingDifficultyModulators ? (
        <Loader />
      ) : (
        <>
          <PatientNavigation patientId={patientId} section={software} />
          {sessionsNavigation()}
          <Title title={t(getProtocolTitle("uppercaseProtocols", protocolId))} marginBottom="10px" />
          {renderSessionDetails()}
          {!chartData ? (
            <IndicatorDataDiv>
              <IndicatorDataText>{t("reports_detailSession_noDifficultyModulatorsMessage")}</IndicatorDataText>
            </IndicatorDataDiv>
          ) : (
            <Graphic
              Title={t("difficultyModulators")}
              Type={Line}
              data={chartData}
              axisY={t("difficultyLevel")}
              axisX={t("reports_difficultyModulatorBySessionGraphic_axisX")}
              max={1}
              forceHideLegend
              graphicDescription={t("graphics_difficultyModulatorsDescription_App_session")}
              legend={<GameModeLegend datasets={chartData.datasets} protocolPrefix={protocolPrefix} tooltipId="sessionDmLegendTip" />}
            />
          )}
        </>
      )}
    </>
  );
};

export default SessionGraphics;
